import { FC, useCallback, useState } from 'react';
import { PERMISSION_ACTIONS, PERMISSION_ROUTES } from 'constants/permissions';
import { App, Button, Collapse, Typography } from 'antd';
import { LANGUAGES } from 'constants/languages';
import { CollapseProps } from 'antd/lib';
import { fetchConfigByType, upsertConfig } from 'services/api/config';

import useOrgEffect from 'hooks/useOrgEffect';
import { CKEditor, Private } from 'components/shared';
import { PageWrapper } from 'components/ui';
import { showErrorMessage } from 'utils/showErrorMessage';

type DataType = {
  [key: string]: string;
};

const { Panel } = Collapse;
const { Text } = Typography;

const TITLES = {
  '1': 'General Conditions of Use',
  '2': 'Privacy policy',
  '4': 'Legal Notice'
};

interface IProps {
  type: '1' | '2' | '4';
}

const StaticPage: FC<IProps> = ({ type }) => {
  const { modal } = App.useApp();
  const [values, setValues] = useState<DataType>();
  const [loading, setLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  useOrgEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const row = await fetchConfigByType(type);
        if (!cancelled) setValues((row?.body as DataType) ?? undefined);
      } catch {
        // swallow — editor stays empty on failure
      }
    };

    load();

    return () => {
      cancelled = true;
      setValues(undefined);
    };
  }, [type, refreshKey]);

  const [error, setError] = useState<string[]>([]);

  const hasEmptyKey = (obj: DataType) => {
    const languages = LANGUAGES.map(lang => lang.locale);

    const emptyKeys = languages.filter(key => !obj[key]);

    return emptyKeys;
  };

  const onFinish = useCallback(async () => {
    if (values) {
      const emptyKeys = hasEmptyKey(values);
      setError(emptyKeys);
      if (emptyKeys.length) return;
    }

    if (error.length) return;

    try {
      setLoading(true);
      await upsertConfig(type, values ?? {});
      setRefreshKey(k => k + 1);
      modal.success({
        title: 'Success',
        content: `${TITLES[type]} page updated successfully!`
      });
    } catch (err) {
      showErrorMessage(err);
    } finally {
      setLoading(false);
    }
  }, [error.length, modal, type, values]);

  const items: CollapseProps['items'] = LANGUAGES.map(item => {
    const hasError = error.includes(item.locale);

    return {
      key: item.key,
      label: item.label,
      forceRender: true,
      children: (
        <Panel
          header={
            <Text
              style={{
                color: `var(--${hasError ? 'error-color' : 'text-color'})`
              }}
            >
              {item.label}
              {hasError ? ' (error)' : ''}
            </Text>
          }
          key={item.locale}
          forceRender
        >
          <CKEditor
            language={item.locale}
            onChange={val =>
              setValues(prev => ({
                ...prev,
                [item.locale]: val
              }))
            }
            value={values?.[item.locale] || ''}
          />
        </Panel>
      )
    };
  });

  return (
    <PageWrapper
      title={TITLES[type]}
      color="white"
      extra={[
        <Private
          key="privacy-policy"
          route={PERMISSION_ROUTES.super}
          action={PERMISSION_ACTIONS.update}
        >
          <Button type="primary" onClick={onFinish} loading={loading}>
            Save
          </Button>
        </Private>
      ]}
    >
      <Collapse
        accordion
        defaultActiveKey={LANGUAGES[0].locale}
        destroyInactivePanel
        items={items}
      />
    </PageWrapper>
  );
};

export default StaticPage;
